/**
 * Tiny stale-while-revalidate cache for read-only API responses.
 *
 * The Apps Script backend adds ~1.5s of unavoidable latency to every call
 * (cold start + the mandatory 302 redirect on POST). Screens that only *read*
 * data don't need to wait for that: we persist the last good response and show
 * it instantly on the next mount, then refresh in the background and swap in the
 * fresh copy when it lands.
 *
 * Cache is keyed by a caller-supplied string and stored in localStorage, so it
 * survives reloads and PWA restarts. Writes that change data should call
 * `bustCache(prefix)` so the next read refetches.
 */

const PREFIX = 'logbook.swr.';

interface Entry<T> {
  data: T;
  ts: number;
}

export function readCache<T>(key: string): { data: T; age: number } | null {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (!raw) return null;
    const e = JSON.parse(raw) as Entry<T>;
    if (!e || typeof e.ts !== 'number') return null;
    return { data: e.data, age: Date.now() - e.ts };
  } catch {
    return null;
  }
}

export function writeCache<T>(key: string, data: T): void {
  try {
    localStorage.setItem(PREFIX + key, JSON.stringify({ data, ts: Date.now() } as Entry<T>));
  } catch {
    /* quota exceeded or storage disabled — cache is best-effort */
  }
}

/** Drop cache entries whose key starts with `prefix` (''=all). Call after a write. */
export function bustCache(prefix = ''): void {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREFIX + prefix)) doomed.push(k);
    }
    doomed.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}

/** Wipe every SWR entry — used on sign-out so the next user starts clean. */
export function clearCache(): void {
  bustCache('');
}
